import { useSignupDispatch } from "components/Signup/store/hooks";
import { useCurrentAnimal } from "components/Signup/store/selectors/animals";
import {
  switchAnimal,
  updateAnimalById,
} from "components/Signup/store/slices/animals";
import { useEffect, useState } from "react";
import { useWebTranslation, Trans } from "translations";
import ChatMessage, { ChatContainer } from "ui-kit/ChatMessage/ChatMessage";
import Gutter from "ui-kit/Gutter";
import { FlexRow } from "ui-kit/Layout/Flex";
import { Typography } from "ui-kit/Typography";
import { wait } from "utils/utils";
import { StepProps } from "../../types";
import { HealthHistoryEnum } from "components/Signup/store/state/types";
import FlowButtons from "components/Signup/components/FlowButtons/FlowButtons";
import { useSequentialAnimals } from "components/Signup/flow/SignupFlow/useSequentialAnimals";

const HealthQuestions = ({
  previous,
  next,
  isValid,
  transitioning,
}: StepProps) => {
  const { t } = useWebTranslation();
  const [note, setNote] = useState<string>("");
  const dispatch = useSignupDispatch();

  const animal = useCurrentAnimal();

  const { nextAnimalId } = useSequentialAnimals();

  const scrollDown = () => {
    wait(0.5).then(() => {
      window.scrollTo({
        top: window.document.body.scrollHeight,
        behavior: "smooth",
      });
    });
  };

  useEffect(() => {
    if (
      animal.healthHistory.includes(HealthHistoryEnum.TREATED_LAST_24_MONTHS)
    ) {
      setNote(
        t("signup_health_questions.ongoing_treatment_note", {
          name: animal.name ?? "",
        })
      );
      scrollDown();
    } else if (
      animal.healthHistory.includes(HealthHistoryEnum.ILLNESS_LAST_24_MONTHS)
    ) {
      setNote(
        t("signup_health_questions.illness_in_24_months_note", {
          name: animal.name ?? "",
        })
      );
      scrollDown();
    } else if (
      animal.healthHistory.includes(HealthHistoryEnum.CHRONIC_DISEASE)
    ) {
      setNote(t("signup_health_questions.chronic_decease_note"));
      scrollDown();
    } else {
      setNote("");
    }
  }, [animal.healthHistory, animal.name, t]);

  const options: { value: HealthHistoryEnum; label: string }[] = [
    {
      value: HealthHistoryEnum.NO_ILLNESS,
      label: t("signup_health_questions.no_illness"),
    },
    {
      value: HealthHistoryEnum.TREATED_LAST_24_MONTHS,
      label: t("signup_health_questions.ongoing_treatment"),
    },
    {
      value: HealthHistoryEnum.ILLNESS_LAST_24_MONTHS,
      label: t("signup_health_questions.illness_in_24_months", {
        name: animal.name ?? "",
      }),
    },
    {
      value: HealthHistoryEnum.CHRONIC_DISEASE,
      label: t("signup_health_questions.chronic_decease"),
    },
  ];

  return (
    <>
      <ChatContainer leaving={transitioning}>
        <ChatMessage>
          <FlexRow>
            <Typography>
              <Trans
                i18nKey="signup_health_questions.health"
                values={{ name: animal.name ?? "" }}
                components={[<strong key="0" />]}
              />
            </Typography>
          </FlexRow>
        </ChatMessage>
        <Gutter size={2} />
        {options.map((item, index) => (
          <ChatMessage
            key={item.value}
            active={animal.healthHistory.includes(item.value)}
            blink
            fromMe
            data-testid={item.label}
            index={index + 1}
            onPress={async () => {
              dispatch(
                updateAnimalById({
                  id: animal.id,
                  update: { healthHistory: [item.value] },
                })
              );
            }}
          >
            <FlexRow alignItems="center">
              <Typography>{item.label}</Typography>
            </FlexRow>
          </ChatMessage>
        ))}
        <Gutter size={0.5} />
        {note && (
          <>
            <ChatMessage index={6}>
              <Typography>{note}</Typography>
            </ChatMessage>
            <Gutter size={4} />
          </>
        )}
        <FlowButtons
          inProgress={transitioning}
          backButton={false}
          next={
            nextAnimalId ? () => dispatch(switchAnimal(nextAnimalId)) : next
          }
          disabled={!isValid}
          previous={previous}
        />
      </ChatContainer>
    </>
  );
};

export default HealthQuestions;
